import { Modal } from "@/components/ui/modal";
import type { EventoAuditoria } from "@/lib/auditoria";

/** formatFecha da un instante ISO en formato local es-ES (día y hora con segundos). */
function formatFecha(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return "—";
  }
  return fecha.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Fila de la ficha: etiqueta a la izquierda, valor a la derecha. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex justify-between gap-4 border-slate-100 border-b py-1.5 last:border-0">
      <span className="text-slate-500">{etiqueta}</span>
      <span className="text-right font-medium text-slate-900">{valor}</span>
    </div>
  );
}

/**
 * EventoDetalle muestra un evento de auditoría completo en un modal: quién, qué, sobre qué
 * registro, en qué obra, desde qué IP y el `detalle` JSON tal como se guardó —que es donde
 * vive el contexto de cada acción y no cabe en la tabla—.
 */
export function EventoDetalle({
  evento,
  onClose,
}: {
  evento: EventoAuditoria | null;
  onClose: () => void;
}) {
  if (evento === null) {
    return null;
  }
  const obra =
    evento.codigo_obra != null
      ? `${evento.codigo_obra}${evento.nombre_obra != null ? ` · ${evento.nombre_obra}` : ""}`
      : "—";

  return (
    <Modal open onClose={onClose} title="Detalle del evento" className="max-w-lg">
      <div className="flex flex-col text-base">
        <Dato etiqueta="Fecha" valor={formatFecha(evento.created_at)} />
        <Dato etiqueta="Usuario" valor={evento.nombre_usuario ?? "—"} />
        <Dato etiqueta="Acción" valor={evento.accion} />
        <Dato etiqueta="Tabla" valor={evento.tabla} />
        <Dato
          etiqueta="Id registro"
          valor={evento.id_registro != null ? String(evento.id_registro) : "—"}
        />
        <Dato etiqueta="Obra" valor={obra} />
        <Dato etiqueta="IP" valor={evento.ip ?? "—"} />
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="font-medium text-slate-700 text-sm">Detalle</span>
        <pre className="max-h-64 overflow-auto rounded-lg bg-slate-50 p-3 text-slate-800 text-sm">
          {JSON.stringify(evento.detalle, null, 2)}
        </pre>
      </div>
    </Modal>
  );
}
